"use client";

import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Link } from "@/i18n/routing";

export type CategoryPill = {
	listingId: string;
	title: string;
	slug: string | null;
};

const SKELETON_COUNT = 5;
const SKELETON_WIDTHS = ["6rem", "8rem", "10rem", "7rem", "9rem"];

const slugToHref = (slug: string | null): string | null => {
	if (!slug) return null;
	if (slug.startsWith("http")) return slug;
	return slug.startsWith("/") ? slug : `/${slug}`;
};

/**
 * Pill list used for Popular / Related Categories. Each pill is the design
 * system `Button` in `solid` + `white` variant — gives the white-background,
 * uppercase chip look from the Figma. 4px gap between pills.
 */
export const CategoryPills = ({
	items,
	loading,
	onItemClick,
}: {
	items: CategoryPill[];
	loading?: boolean;
	onItemClick?: () => void;
}) => {
	if (loading && !items.length) {
		return (
			<ul className="flex flex-wrap gap-1">
				{Array.from({ length: SKELETON_COUNT }).map((_, i) => (
					<li key={`pill-skel-${i}`}>
						<Skeleton
							className="h-8 rounded-radius-component-specific-button"
							width={SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]}
						/>
					</li>
				))}
			</ul>
		);
	}

	if (!items.length) return null;

	return (
		<ul className="flex flex-wrap gap-1">
			{items.map((item) => {
				const href = slugToHref(item.slug);
				const button = (
					<Button variant="solid" color="white" size="medium">
						{item.title}
					</Button>
				);
				return (
					<li key={item.listingId}>
						{href ? (
							<Link href={href} onClick={onItemClick} className="unstyled inline-flex">
								{button}
							</Link>
						) : (
							button
						)}
					</li>
				);
			})}
		</ul>
	);
};
